import {ADMembersRepo} from './ADMembersRepo'
import FetchWrapper from '../../../../Network/FetchWrapper'
import {CommonResponseDTO, CommonResponseJSON} from '../../../../Network/CommonResponse'
import {BucketMemberInfoDTO, BucketMemberInfoJSON} from '../DTO/BucketMemberInfoDTO'
import {Nullable} from '../../../../Common/TypeHelper'
import {BucketInfoDTO, BucketInfoJSON} from '../DTO/BucketInfoDTO'
import {MemberSearchParams, MemberSearchResultDTO, MemberSearchResultJson} from '../DTO/MemberSearchResultDTO'
import {UrlUtility} from '../../../Utils/UrlUtility'
import {ContractHistoryDTO, ContractHistoryJSON} from '../DTO/ContractHistoryDTO'
import {MemberInfoDTO, MemberInfoJSON} from '../DTO/MemberInfoDTO'
import {withAdminsAuth} from '../../../../Network/BrowserFetchWrapper'
import {ShippingAddressInfoDTO, ShippingAddressInfoJSON} from '../DTO/ShippingAddressInfoDTO'
import {CurrentMemberDenyAttributeDTO, CurrentMemberDenyAttributeJSON} from '../DTO/CurrentMemberDenyAttributeDTO'
import {MemberDenyAttributeHistoryDTO, MemberDenyAttributeHistoryJSON} from '../DTO/MemberDenyAttributeHistoryDTO'
import {UpdateMemberDenyAttributeDTO} from '../DTO/UpdateMemberDenyAttributeDTO'
import {DTO} from '../../../../Common/DtoUtilities'
import {DenyInfoDTO, DenyInfoJSON, UnionDenyInfoDTO, UnionDenyInfoJSON} from '../DTO/DenyInfoDTO'
import {GmoOrderIdDTO} from '../DTO/GmoOrderIdDTO'
import {ExtendMetaDataDTO, ExtendMetaDataJSON} from '../DTO/ExtendMetaDataDTO'
import {UpdateMemberBasicInfoDTO} from '../DTO/UpdateMemberBasicInfoDTO'
import {UpdateMemberAddressDTO} from '../DTO/UpdateMemberAddressDTO'
import {UpdateShippingAddressDTO} from '../DTO/UpdateShippingAddressDTO'
import {UpdateExtendMetaDataDTO} from '../DTO/UpdateExtendMetaDataDTO'
import {UpdateBucketOptionDTO} from '../DTO/UpdateBucketOptionDTO'

export class NetworkADMembersRepo implements ADMembersRepo {
  private fetchWrapper: FetchWrapper
  private serverUrl = process.env.REACT_APP_MEMBUCKET_ADMIN_APP_SERVER_URL

  constructor(fetchWrapper: FetchWrapper) {
    this.fetchWrapper = fetchWrapper
  }

  getMembers(
    bucketKey: string,
    limit: number,
    params: MemberSearchParams,
  ): Promise<CommonResponseDTO<Nullable<MemberSearchResultDTO>>> {
    const queryParams = {
      bucket_key: bucketKey,
      limit: limit,
      bucket_member_id: params.bucketMemberId,
      mail: encodeURIComponent(params.mail),
      name: encodeURIComponent(params.name),
      kana: encodeURIComponent(params.kana),
      tel: params.tel,
      in_leave_member: params.inLeaveMember,
    }
    return this.fetchWrapper.getByJson(
      UrlUtility.concatUrlAndQueryParams(`${this.serverUrl}/v1/a/r/members`, queryParams),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<MemberSearchResultJson>) => {
      return CommonResponseDTO.from(
        response,
        MemberSearchResultDTO.fromJSON,
      )
    })
  }

  getMemberInfo(memberId: number): Promise<CommonResponseDTO<Nullable<MemberInfoDTO>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}`,
      withAdminsAuth(),
    )
      .then((response: CommonResponseJSON<MemberInfoJSON>) => {
        return CommonResponseDTO.from(
          response,
          MemberInfoDTO.fromJSON,
        )
      })
  }

  getBucketMemberInfoList(
    bucketKey: string,
    memberId: number,
  ): Promise<CommonResponseDTO<Nullable<BucketMemberInfoDTO[]>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/bucket_contract?bucket_key=${bucketKey}`,
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<BucketMemberInfoJSON[]>) => {
      return CommonResponseDTO.fromArray(
        response,
        BucketMemberInfoDTO.fromJSON,
      )
    })
  }

  getBucketInfo(
    bucketKey: string,
  ): Promise<CommonResponseDTO<Nullable<BucketInfoDTO>>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/a/p/buckets/${bucketKey}`)
      .then(
        (response: CommonResponseJSON<BucketInfoJSON & { key: string }>) => {
          const {status, data, errorMessageList} = response
          return CommonResponseDTO.from(
            {
              status: status,
              data: {...data, key: bucketKey},
              errorMessageList: errorMessageList,
            },
            BucketInfoDTO.fromJSON,
          )
        },
      )
  }

  getContractHistoryListByMemberId(
    bucketKey: string,
    memberId: number,
  ): Promise<CommonResponseDTO<Nullable<ContractHistoryDTO[]>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/contract_history?bucket_key=${bucketKey}`,
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<ContractHistoryJSON[]>) => {
      return CommonResponseDTO.fromArray(
        response,
        ContractHistoryDTO.fromJSON,
      )
    })
  }

  getShippingAddressInfoList(
    memberId: number,
    bucketKey: string,
  ): Promise<CommonResponseDTO<Nullable<ShippingAddressInfoDTO[]>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/shipping_addresses?bucket_key=${bucketKey}`,
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<ShippingAddressInfoJSON[]>) => {
        return CommonResponseDTO.fromArray(
          response,
          ShippingAddressInfoDTO.fromJSON,
        )
      },
    )
  }

  getMemberExtendMetaData(
    memberId: number,
  ): Promise<CommonResponseDTO<Nullable<ExtendMetaDataDTO[]>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/extend_metadata`,
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<ExtendMetaDataJSON[]>) => {
        return CommonResponseDTO.fromArray(
          response,
          ExtendMetaDataDTO.fromJSON,
        )
      },
    )
  }

  getCurrentMemberDenyAttribute(
    memberId: number,
    bucketKey: string,
  ): Promise<CommonResponseDTO<Nullable<CurrentMemberDenyAttributeDTO>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/extension_params?bucket_key=${bucketKey}`,
      withAdminsAuth(),
    ).then((response: CommonResponseDTO<CurrentMemberDenyAttributeJSON>) => {
      return CommonResponseDTO.from(
        response,
        CurrentMemberDenyAttributeDTO.fromJSON,
      )
    })
  }

  getMemberDenyAttributeHistoryListByMemberId(
    memberId: number,
    bucketKey: string,
  ): Promise<CommonResponseDTO<Nullable<MemberDenyAttributeHistoryDTO[]>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/extension_params_history?bucket_key=${bucketKey}`,
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<MemberDenyAttributeHistoryJSON[]>) => {
      return CommonResponseDTO.fromArray(
        response,
        MemberDenyAttributeHistoryDTO.fromJSON,
      )
    })
  }

  putMemberBasicInfo(
    memberId: number,
    updateMemberBasicInfoDTO: UpdateMemberBasicInfoDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/basic_info`,
      DTO.toObject(updateMemberBasicInfoDTO),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  putMemberAddress(
    memberId: number,
    updateMemberAddressDTO: UpdateMemberAddressDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/address`,
      DTO.toObject(updateMemberAddressDTO),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  postShippingAddress(
    memberId: number,
    updateShippingAddressInfoDTO: UpdateShippingAddressDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.postJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/shipping_addresses`,
      DTO.toObject(updateShippingAddressInfoDTO),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  putShippingAddress(
    memberId: number,
    shippingAddressId: number,
    updateShippingAddressInfoDTO: UpdateShippingAddressDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/shipping_addresses/${shippingAddressId}`,
      DTO.toObject(updateShippingAddressInfoDTO),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  deleteShippingAddress(
    memberId: number,
    shippingAddressId: number,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.deleteJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/shipping_addresses/${shippingAddressId}`,
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  putMemberExtendMetaData(
    memberId: number,
    updateExtendMetaDataDTO: UpdateExtendMetaDataDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/extend_metadata`,
      DTO.toObject(updateExtendMetaDataDTO),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  putMemberBucketOption(
    memberId: number,
    bucketKey: string,
    updateBucketOptionDTO: UpdateBucketOptionDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/bucket_options?bucket_key=${bucketKey}`,
      DTO.toObject(updateBucketOptionDTO),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  postMemberDenyAttribute(
    memberId: number,
    updateMemberDenyAttributeDto: UpdateMemberDenyAttributeDTO,
  ): Promise<CommonResponseDTO<Nullable<number>>> {
    return this.fetchWrapper.postJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/extension_params`,
      DTO.toObject(updateMemberDenyAttributeDto),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<Nullable<number>>) => {
      return CommonResponseDTO.from(
        response,
        CommonResponseDTO.NO_CONVERT,
      )
    })
  }

  getDenyAttributeList(bucketKey: string): Promise<CommonResponseDTO<Nullable<DenyInfoDTO[]>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/a/r/${bucketKey}/extension_params`,
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<DenyInfoJSON[]>) => {
      return CommonResponseDTO.fromArray(
        response,
        DenyInfoDTO.fromJSON,
      )
    })
  }

  getUnionDenyAttributeList(
    bucketKey: string,
  ): Promise<CommonResponseDTO<Nullable<UnionDenyInfoDTO[]>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/a/r/${bucketKey}/union_extension_params`,
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<UnionDenyInfoJSON[]>) => {
      return CommonResponseDTO.fromArray(
        response,
        UnionDenyInfoDTO.fromJSON,
      )
    })
  }

  postReserveLeaveBucket(
    memberId: number,
    bucketKey: string,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.postJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/buckets/${bucketKey}/leave`,
      DTO.toObject({leaveMode: "reserve"}),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  postFailedMobileCarrierForcedLeave(
    memberId: number,
    bucketKey: string,
    gmoOrderId: GmoOrderIdDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.postJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}/buckets/${bucketKey}/leave`,
      DTO.toObject({leaveMode: "force", ...gmoOrderId}),
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  deleteMemberAccount(
    memberId: number
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.deleteJson(
      `${this.serverUrl}/v1/a/r/members/${memberId}`,
      withAdminsAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }
}
